import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Application constants and utilities
export const APP_CONSTANTS = {
  DEFAULT_PLANNING_MONTHS: 22,
  MAX_SALARY: 10000000, // 1 crore INR
  MIN_SALARY: 0,
  MAX_HEADCOUNT_PER_ROLE: 1000,
  MIN_HEADCOUNT_PER_ROLE: 0,
} as const;

/**
 * Creates an array filled with a default value
 */
export const createEmptyArray = <T>(length: number, defaultValue: T): T[] => {
  return Array(length).fill(defaultValue);
};

/**
 * Validates salary input
 */
export const validateSalary = (value: number): number => {
  return Math.max(APP_CONSTANTS.MIN_SALARY, Math.min(APP_CONSTANTS.MAX_SALARY, value));
};

/**
 * Validates headcount input
 */
export const validateHeadcount = (value: number): number => {
  return Math.max(APP_CONSTANTS.MIN_HEADCOUNT_PER_ROLE, Math.min(APP_CONSTANTS.MAX_HEADCOUNT_PER_ROLE, value));
};

/**
 * Safely formats currency values
 */
export const formatCurrency = (amount: number): string => {
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(amount);
  } catch (error) {
    console.warn('Currency formatting failed:', error);
    return `₹${amount.toLocaleString()}`;
  }
};

/**
 * Safely performs mathematical operations with error handling
 */
export const safeMath = {
  add: (a: number, b: number): number => {
    const result = a + b;
    if (!isFinite(result)) {
      console.warn('Addition resulted in non-finite number:', { a, b, result });
      return 0;
    }
    return result;
  },

  multiply: (a: number, b: number): number => {
    const result = a * b;
    if (!isFinite(result)) {
      console.warn('Multiplication resulted in non-finite number:', { a, b, result });
      return 0;
    }
    return result;
  },

  divide: (a: number, b: number): number => {
    if (b === 0) {
      console.warn('Division by zero attempted:', { a, b });
      return 0;
    }
    const result = a / b;
    if (!isFinite(result)) {
      console.warn('Division resulted in non-finite number:', { a, b, result });
      return 0;
    }
    return result;
  },
};